/**
 * PATH: apps/qa/src/pages/ReportEditorPage.tsx
 * PURPOSE: Section-based editable report editor (DRE Research pattern)
 *
 * FLOW:
 * ┌──────────────────┐   ┌──────────────────┐   ┌──────────────┐
 * │ Section sidebar   │   │ Edit/Preview     │   │ Export       │
 * │ (navigate, status)│──▶│ (textarea/render) │──▶│ (PDF/Word)   │
 * └──────────────────┘   └──────────────────┘   └──────────────┘
 *
 * DEPENDENCIES:
 * - ./report-editor/*: extracted sub-components and constants
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getApiBoundaryState, isDraftConflict, reportDraftApi, type ReportDraftItem } from '@/lib/api'
import { cn } from '@/lib/utils'
import { SECTION_ORDER, getSectionDescription, getSectionLabel, STATUS_STYLES } from './report-editor/constants'
import { SectionSidebar } from './report-editor/SectionSidebar'
import { ReportDocumentEditor } from './report-editor/ReportDocumentEditor'
import { OverrideHistory } from './report-editor/OverrideHistory'
import {
  FileText, Edit3, Eye, Check, X, Loader2, Download, ChevronLeft,
  Copy, Sparkles, FileDown, AlertTriangle, Printer,
} from 'lucide-react'

export default function ReportEditorPage() {
  const { id: draftId } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const [activeSection, setActiveSection] = useState<string>('cover')
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [activeView, setActiveView] = useState<'compose' | 'pdf'>('compose')
  const [isCopied, setIsCopied] = useState(false)
  const [sectionOrder, setSectionOrder] = useState<string[]>(SECTION_ORDER)
  const [draggedSection, setDraggedSection] = useState<string | null>(null)
  const [isPdfGenerating, setIsPdfGenerating] = useState(false)
  const [exactRevisionId, setExactRevisionId] = useState<string | null>(null)

  const { data: draft, isLoading, error } = useQuery({
    queryKey: ['qa-report-draft', draftId],
    queryFn: () => reportDraftApi.getDraft(draftId!),
    enabled: !!draftId,
  })
  useEffect(() => {
    if (!draft?.sections.length) return
    const ordered = [...draft.sections].sort((a, b) => a.sort_order - b.sort_order).map((section) => section.section_type)
    setSectionOrder(ordered)
    if (!ordered.includes(activeSection)) setActiveSection(ordered[0])
  }, [draft?.sections, activeSection])

  const updateSectionMutation = useMutation({
    mutationFn: async ({ sectionType, content }: { sectionType: string; content: string }) => {
      const expectedVersion = draft!.version
      await reportDraftApi.updateSection(draftId!, sectionType, content, expectedVersion)
      return reportDraftApi.saveVersion(draftId!, {
        expected_version: expectedVersion + 1,
      })
    },
    onSuccess: () => { setIsEditing(false); setOverrideReason(''); queryClient.invalidateQueries({ queryKey: ['qa-report-draft', draftId] }) },
  })
  const overrideMutation = useMutation({
    mutationFn: ({ sectionType, original, current, reason }: { sectionType: string; original: string; current: string; reason: string }) =>
      reportDraftApi.createOverride(draftId!, sectionType, {
        original_content: original,
        current_content: current,
        reason,
        expected_version: draft!.version,
      }),
    onSuccess: ({ draft_version, override }, variables) => {
      queryClient.setQueryData<ReportDraftItem>(['qa-report-draft', draftId], (current) => current ? {
        ...current,
        version: draft_version,
        fact_overrides: [...(current.fact_overrides || []), override],
        sections: current.sections.map((section) => section.section_type === variables.sectionType
          ? { ...section, content: variables.current, is_auto_generated: false }
          : section),
      } : current)
      queryClient.invalidateQueries({ queryKey: ['qa-report-overrides', draftId, activeSection] })
      setIsEditing(false)
      setOverrideReason('')
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: (newStatus: string) => reportDraftApi.updateDraft(draftId!, { status: newStatus, expected_version: draft!.version }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-report-draft', draftId] }) },
  })

  const exportMarkdownMutation = useMutation({
    mutationFn: () => reportDraftApi.exportMarkdown(draftId!),
    onSuccess: (data) => {
      const blob = new Blob([data.markdown], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = `${data.title.replace(/\s+/g, '_')}.md`; a.click()
      URL.revokeObjectURL(url)
    },
  })

  const exportPdfMutation = useMutation({
    mutationFn: () => reportDraftApi.exportPdf(draftId!),
    onSuccess: (data) => {
      if (data.status === 'generated') {
        setIsPdfGenerating(false)
        setExactRevisionId(data.revision_id || null)
        queryClient.setQueryData<ReportDraftItem>(['qa-report-draft', draftId], (current) => current ? {
          ...current,
          status: 'exported',
          exported_report: data.report_id || current.exported_report,
        } : current)
        queryClient.invalidateQueries({ queryKey: ['qa-report-draft', draftId] })
        setActiveView('pdf')
      } else if (data.status === 'generating' || data.status === 'queued') {
        setIsPdfGenerating(true)
        const pollInterval = setInterval(async () => {
          try {
            const updated = await reportDraftApi.getDraft(draftId!)
            if (updated.status === 'exported') {
              clearInterval(pollInterval); setIsPdfGenerating(false)
              await queryClient.invalidateQueries({ queryKey: ['qa-report-draft', draftId] }); setActiveView('pdf')
            }
          } catch { /* Keep polling */ }
        }, 3000)
        setTimeout(() => { clearInterval(pollInterval); setIsPdfGenerating(false) }, 120_000)
      }
    },
  })

  const currentSection = useMemo(() => draft?.sections.find((s) => s.section_type === activeSection), [draft, activeSection])
  const sectionStats = useMemo(() => {
    if (!draft?.sections) return { completed: 0, total: SECTION_ORDER.length }
    return { completed: draft.sections.filter((s) => s.content && s.content.trim().length > 0).length, total: SECTION_ORDER.length }
  }, [draft])

  const handleStartEdit = useCallback(() => { if (currentSection) { setEditContent(currentSection.content); setIsEditing(true) } }, [currentSection])
  const handleSaveEdit = useCallback(() => {
    if (currentSection?.is_auto_generated && !overrideReason.trim()) return
    if (currentSection?.is_auto_generated) {
      overrideMutation.mutate({ sectionType: activeSection, original: currentSection.content, current: editContent, reason: overrideReason.trim() })
      return
    }
    updateSectionMutation.mutate({ sectionType: activeSection, content: editContent })
  }, [activeSection, currentSection, editContent, overrideReason, overrideMutation, updateSectionMutation])
  const handleCancelEdit = useCallback(() => { setIsEditing(false); setEditContent('') }, [])
  const handleCopySection = useCallback(() => { if (currentSection?.content) { navigator.clipboard.writeText(currentSection.content); setIsCopied(true); setTimeout(() => setIsCopied(false), 2000) } }, [currentSection])

  const handleDragStart = useCallback((sectionType: string) => setDraggedSection(sectionType), [])
  const handleDragOver = useCallback((e: React.DragEvent, targetSection: string) => {
    e.preventDefault()
    if (!draggedSection || draggedSection === targetSection) return
    setSectionOrder(prev => { const n = [...prev]; const from = n.indexOf(draggedSection); const to = n.indexOf(targetSection); if (from === -1 || to === -1) return prev; n.splice(from, 1); n.splice(to, 0, draggedSection); return n })
  }, [draggedSection])
  const handleDragEnd = useCallback(() => setDraggedSection(null), [])

  const exactPreviewUrl = draft?.exported_report || exactRevisionId ? reportDraftApi.getExactPreviewUrl(draftId!, exactRevisionId || undefined) : null
  const saveError = overrideMutation.error || updateSectionMutation.error
  const mutationPending = overrideMutation.isPending || updateSectionMutation.isPending

  if (isLoading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
  if (error || !draft) return (
    <div className="flex items-center justify-center min-h-[60vh]"><div className="text-center">
      <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" /><p className="text-foreground font-medium">Report draft not found</p>
      <Link to="/reports" className="text-primary text-sm hover:underline mt-2 inline-block">Back to Reports</Link>
    </div></div>
  )

  return (
    <div className="flex flex-col h-full">
      {/* Top Header */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <Link to="/reports" className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"><ChevronLeft className="w-5 h-5" /></Link>
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center"><FileText className="h-5 w-5 text-primary-foreground" /></div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">{draft.title}</h1>
            <p className="text-xs text-muted-foreground">Job #{draft.job_id} · {sectionStats.completed}/{sectionStats.total} sections · <span className={cn('px-1.5 py-0.5 rounded-full text-[10px] font-medium', STATUS_STYLES[draft.status])}>{draft.status}</span></p>
          </div>
        </div>
        <div className="hidden items-center gap-2 md:flex">
          {draft.status === 'draft' && <button onClick={() => updateStatusMutation.mutate('review')} disabled={updateStatusMutation.isPending} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-500 text-sm font-medium transition-colors">Mark for Review</button>}
          {draft.status === 'review' && <button onClick={() => updateStatusMutation.mutate('final')} disabled={updateStatusMutation.isPending} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 text-sm font-medium transition-colors">Finalise</button>}
          <div className="relative group">
            <button className="px-3 py-1.5 rounded-lg bg-muted text-foreground hover:bg-accent text-sm font-medium flex items-center gap-1.5 transition-colors">
              {isPdfGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{isPdfGenerating ? 'Generating...' : 'Export'}
            </button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <div className="p-1">
                <button onClick={() => exportPdfMutation.mutate()} disabled={exportPdfMutation.isPending || isPdfGenerating} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent rounded transition-colors disabled:opacity-50">
                  {isPdfGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}{isPdfGenerating ? 'Generating PDF...' : 'PDF Export'}
                </button>
                <button onClick={() => exportMarkdownMutation.mutate()} disabled={exportMarkdownMutation.isPending} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent rounded transition-colors disabled:opacity-50"><FileDown className="h-4 w-4" />Markdown (.md)</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 flex border-b border-border bg-card px-4" role="tablist" aria-label="Report views">
        <button type="button" role="tab" aria-selected={activeView === 'compose'} onClick={() => setActiveView('compose')} className={cn('min-h-11 border-b-2 px-4 text-sm font-medium', activeView === 'compose' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground')}>Compose</button>
        <button type="button" role="tab" aria-selected={activeView === 'pdf'} onClick={() => setActiveView('pdf')} className={cn('min-h-11 border-b-2 px-4 text-sm font-medium', activeView === 'pdf' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground')}>Exact PDF preview</button>
      </div>
      {(isDraftConflict(saveError) || isDraftConflict(updateStatusMutation.error) || isDraftConflict(exportPdfMutation.error)) && <div role="alert" className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"><span>This draft changed or became immutable. Your edit was not overwritten.</span><button type="button" onClick={() => { setIsEditing(false); queryClient.invalidateQueries({ queryKey: ['qa-report-draft', draftId] }) }} className="rounded-lg border border-amber-400 px-3 py-1.5 font-medium">Reload latest</button></div>}
      {exportPdfMutation.isError && !isDraftConflict(exportPdfMutation.error) && <BoundaryError error={exportPdfMutation.error} action="generate the exact PDF" />}
      {updateStatusMutation.isError && !isDraftConflict(updateStatusMutation.error) && <BoundaryError error={updateStatusMutation.error} action="change the report status" />}

      {activeView === 'pdf' ? (
        <div className="flex-1 bg-muted p-4">
          {exactPreviewUrl ? <iframe title="Backend-generated exact PDF preview" src={exactPreviewUrl} className="h-full min-h-[70vh] w-full rounded-lg border border-border bg-white" /> : (
            <div className="flex h-full min-h-[60vh] flex-col items-center justify-center text-center">
              <FileText className="h-12 w-12 text-muted-foreground" /><h2 className="mt-3 font-semibold text-foreground">No issued PDF artifact yet</h2>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">Generate the backend PDF first. This tab intentionally never substitutes a browser rendering for the exact artifact.</p>
              <button onClick={() => exportPdfMutation.mutate()} disabled={exportPdfMutation.isPending || isPdfGenerating} className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">Generate exact PDF</button>
            </div>
          )}
        </div>
      ) : (
      <div className="flex-1 flex min-h-0">
        <SectionSidebar sectionOrder={sectionOrder} activeSection={activeSection} draggedSection={draggedSection} draft={draft}
          onSelectSection={(s) => { if (isEditing) handleCancelEdit(); setActiveSection(s) }}
          onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} />

        <div className="flex-1 flex flex-col min-w-0">
          {/* Section Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-border">
            <div className="min-w-0">
              <select value={activeSection} onChange={(event) => setActiveSection(event.target.value)} className="mb-1 max-w-full rounded-lg border border-input bg-background px-2 py-1 text-sm font-medium md:hidden" aria-label="Report section">
                {sectionOrder.map((section) => <option key={section} value={section}>{getSectionLabel(section)}</option>)}
              </select>
              <h2 className="hidden text-lg font-semibold text-foreground md:block">{getSectionLabel(activeSection)}</h2><p className="text-xs text-muted-foreground mt-0.5">{getSectionDescription(activeSection)}</p>
            </div>
            <div className="flex items-center gap-2">
              {currentSection?.content && <span className="text-xs text-muted-foreground mr-2">{currentSection.word_count} words</span>}
              <button onClick={handleCopySection} disabled={!currentSection?.content} className="p-2 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50" title="Copy">{isCopied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}</button>
              {isEditing ? (
                <>
                  <button onClick={handleCancelEdit} className="px-3 py-1.5 rounded-lg bg-muted text-foreground hover:bg-accent text-sm font-medium flex items-center gap-1"><X className="h-4 w-4" />Cancel</button>
                  <button onClick={handleSaveEdit} disabled={mutationPending || Boolean(currentSection?.is_auto_generated && !overrideReason.trim())} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 text-sm font-medium flex items-center gap-1 disabled:opacity-50">
                    {mutationPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Save
                  </button>
                </>
              ) : (
                <>
                  <button onClick={handleStartEdit} disabled={!currentSection} className="hidden md:flex px-3 py-1.5 rounded-lg bg-muted text-foreground hover:bg-accent text-sm font-medium items-center gap-1.5 disabled:opacity-50"><Edit3 className="h-4 w-4" />Edit</button>
                  <button className="p-2 rounded-lg bg-primary/10 text-primary transition-colors"><Eye className="h-4 w-4" /></button>
                </>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            {isEditing ? (
              <div className="h-full overflow-y-auto bg-muted/30 p-3 md:p-6">
                {currentSection?.is_auto_generated && <><OverrideHistory draftId={draftId!} sectionType={activeSection} /><label className="mx-auto mb-3 block max-w-4xl text-sm font-medium text-foreground">Audited override reason <span className="text-destructive">*</span><textarea value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2" placeholder="Explain why generated QA content is being changed" /><span className="mt-1 block text-xs font-normal text-muted-foreground">The override API receives the immutable original, current replacement, and reason.</span></label></>}
                <ReportDocumentEditor content={editContent} title={getSectionLabel(activeSection)} onChange={setEditContent} onOpenExactPdfPreview={() => setActiveView('pdf')} />
                {saveError && !isDraftConflict(saveError) && <BoundaryError error={saveError} action={currentSection?.is_auto_generated ? 'record the audited override' : 'save this report section'} compact />}
              </div>
            ) : currentSection?.content ? (
              <div className="h-full overflow-y-auto p-3 md:p-6"><div className="max-w-5xl mx-auto">
                {currentSection.is_auto_generated && (
                  <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">This section was auto-populated from inspection data. Click <strong>Edit</strong> to customise.</p>
                  </div>
                )}
                <ReportDocumentEditor content={currentSection.content} title={getSectionLabel(activeSection)} readOnly onChange={() => undefined} onOpenExactPdfPreview={() => setActiveView('pdf')} />
                {currentSection.last_edited_by_name && <p className="text-[10px] text-muted-foreground mt-6">Last edited by {currentSection.last_edited_by_name}{currentSection.last_edited_at && ` · ${new Date(currentSection.last_edited_at).toLocaleDateString()}`}</p>}
              </div></div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center py-16 text-center">
                <div className="h-16 w-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center"><FileText className="h-8 w-8 text-muted-foreground" /></div>
                <h3 className="text-lg font-medium text-foreground mb-2">No content yet</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">Click Edit to write content for this section</p>
                <button onClick={() => { setEditContent(''); setIsEditing(true) }} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium flex items-center gap-2"><Edit3 className="h-4 w-4" />Write Content</button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  )
}

function BoundaryError({ error, action, compact = false }: { error: unknown; action: string; compact?: boolean }) {
  const state = getApiBoundaryState(error)
  const message = state === 'forbidden' ? `Permission denied: you cannot ${action}.`
    : state === 'invalid' ? `The backend rejected the data required to ${action}. Check all required fields.`
      : `Could not ${action}.`
  return <p role="alert" className={`${compact ? 'mx-auto mt-3 max-w-4xl' : 'border-b border-destructive/30'} bg-destructive/10 p-3 text-sm text-destructive`}>{message}</p>
}
